import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { coursesApi } from '@/api/courses';
import { usersApi } from '@/api/users';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthStore } from '@/stores/auth';
import { humanizeError } from '@/lib/errors';

type AdminSummary = {
  active_students: number;
  total_students: number;
  completed_lessons: number;
  revenue_total: number;
};

// Status and language label maps are resolved via t() inside the component below.

/**
 * Admin landing — a quick "command center" view with KPIs and shortcuts.
 * Headline KPIs come from the analytics summary endpoint
 * (`/admin/analytics/summary`, progress-service §3.8); list queries below back
 * the marathon/curator counts and degrade softly if analytics is unavailable.
 */
export function AdminDashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const STATUS_RU: Record<string, string> = {
    draft: t('marathon.statusDraft'),
    published: t('marathon.statusPublished'),
    archived: t('marathon.statusArchived'),
  };
  const LANG_RU: Record<string, string> = {
    KZ: t('marathon.langKZ'),
    RU: t('marathon.langRU'),
    EN: t('marathon.langEN'),
  };

  const summary = useQuery({
    queryKey: ['admin', 'dashboard-summary'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/admin/analytics/summary');
        return data as AdminSummary;
      } catch {
        return null;
      }
    },
  });

  const marathons = useQuery({
    queryKey: ['admin', 'marathons-summary'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 5, template: 'false' }),
  });

  const students = useQuery({
    queryKey: ['admin', 'students-summary'],
    queryFn: () => usersApi.list({ role: 'student', limit: 1 }),
  });

  const curators = useQuery({
    queryKey: ['admin', 'curators-summary'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 1 }),
  });

  return (
    <>
      <PageHeader
        title={t('adminDashboard.greeting', { name: user?.full_name?.split(' ')[0] ?? t('adminDashboard.defaultName') })}
        subtitle={t('adminDashboard.subtitle')}
      />

      {/* KPI tiles */}
      <section className="grid gap-4 md:grid-cols-3">
        <KpiCard
          label={t('adminDashboard.kpiMarathons')}
          value={marathons.data?.total ?? '—'}
          icon="school"
          to="/admin/marathons"
        />
        <KpiCard
          label={t('adminDashboard.kpiStudents')}
          value={students.data?.total ?? '—'}
          icon="groups"
          to="/admin/students"
        />
        <KpiCard
          label={t('adminDashboard.kpiCurators')}
          value={curators.data?.total ?? '—'}
          icon="co_present"
          to="/admin/curators"
        />
      </section>

      {/* Live platform metrics (analytics §3.8) */}
      <section className="mt-4 grid gap-4 md:grid-cols-2">
        <KpiCard
          label={t('adminDashboard.kpiActive')}
          value={summary.data?.active_students ?? '—'}
          icon="bolt"
          to="/admin/stats"
        />
        <KpiCard
          label={t('adminDashboard.kpiCompletedLessons')}
          value={summary.data?.completed_lessons ?? '—'}
          icon="check_circle"
          to="/admin/stats"
        />
      </section>

      {/* Recent marathons */}
      <section className="mt-10">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-display text-on-surface">
            {t('adminDashboard.recentMarathons')}
          </h2>
          <Link to="/admin/marathons" className="text-sm font-semibold text-primary">
            {t('adminDashboard.viewAll')}
          </Link>
        </div>

        {marathons.isLoading ? (
          <Card variant="subtle">{t('common.loading')}</Card>
        ) : marathons.isError ? (
          <Card variant="subtle" className="text-on-surface-variant">
            {humanizeError(marathons.error)}
          </Card>
        ) : ((marathons.data?.items ?? []).length) === 0 ? (
          <Card variant="subtle" className="text-center text-on-surface-variant">
            {t('adminDashboard.noMarathons')}{' '}
            <Link to="/admin/marathons" className="font-semibold text-primary">
              {t('adminDashboard.createFirst')}
            </Link>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {(marathons.data?.items ?? []).map((m) => (
              <Link key={m.id} to={`/admin/marathons/${m.id}`}>
                <Card interactive className="h-full">
                  <CardTitle>{m.title}</CardTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Pill variant={m.status === 'published' ? 'success' : m.status === 'archived' ? 'neutral' : 'warning'}>
                      {STATUS_RU[m.status] ?? m.status}
                    </Pill>
                    <Pill variant="primary">{m.level_start} → {m.level_end}</Pill>
                    <Pill variant="neutral">{LANG_RU[m.language] ?? m.language}</Pill>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Shortcuts */}
      <section className="mt-10">
        <h2 className="mb-4 font-display text-2xl font-bold tracking-display text-on-surface">
          {t('adminDashboard.quickActions')}
        </h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <ShortcutCard to="/admin/marathons" icon="add_box" label={t('adminDashboard.shortcutCreateMarathon')} />
          <ShortcutCard to="/admin/students" icon="person_add" label={t('adminDashboard.shortcutAddStudent')} />
          {/* Сторис — и на сайте, и в приложении с 1.1.2 (сторис 2.0, 13.10.2026). Ярлыка «Тарифы и скидки»
              нет с 13.10.2026: раздел «Тарифы» убран целиком (решение владельца). */}
          <ShortcutCard to="/admin/stories" icon="photo_camera" label={t('adminDashboard.shortcutPublishStory')} />
        </div>
      </section>
    </>
  );
}

function KpiCard({ label, value, icon, to }: { label: string; value: string | number; icon: string; to: string }) {
  return (
    <Link to={to}>
      <Card interactive className="flex items-center gap-4">
        <span className="material-symbols-outlined rounded-button bg-primary-fixed p-2 text-2xl text-on-primary-fixed-variant">
          {icon}
        </span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {label}
          </p>
          <p className="font-display text-3xl font-extrabold tracking-display text-on-surface">
            {value}
          </p>
        </div>
      </Card>
    </Link>
  );
}

function ShortcutCard({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <Link to={to}>
      <Card variant="subtle" interactive className="flex items-center gap-3">
        <span className="material-symbols-outlined text-2xl text-primary">{icon}</span>
        <span className="font-semibold text-on-surface">{label}</span>
      </Card>
    </Link>
  );
}
